import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useOrganization } from '@/contexts/OrganizationContext';

/**
 * Hook for navigation that automatically includes the current org in the path.
 * Use this for absolute navigation within the org scope.
 *
 * @example
 * const orgNavigate = useOrgNavigate();
 * orgNavigate('/dashboard'); // navigates to /org/{orgId}/dashboard
 * orgNavigate('/projects'); // navigates to /org/{orgId}/projects
 */
export function useOrgNavigate() {
  const { selectedOrganization } = useOrganization();
  const navigate = useNavigate();

  // `state` rides through to react-router's navigate — used by deep links
  // (e.g. the AP overview tile opening a bill's editor on the Payables page).
  return useCallback((path: string, options?: { replace?: boolean; state?: unknown }) => {
    if (!selectedOrganization) {
      // No org selected - redirect to org select
      navigate('/org-select', options);
      return;
    }

    // Ensure path starts with /
    const normalizedPath = path.startsWith('/') ? path : `/${path}`;
    navigate(`/org/${selectedOrganization.id}${normalizedPath}`, options);
  }, [selectedOrganization, navigate]);
}

/**
 * Returns the base path for the current org.
 * Useful for constructing links.
 *
 * @example
 * const orgBasePath = useOrgBasePath();
 * <Link to={`${orgBasePath}/projects`}>Projects</Link>
 */
export function useOrgBasePath() {
  const { selectedOrganization } = useOrganization();
  return selectedOrganization ? `/org/${selectedOrganization.id}` : '';
}
